import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import { IListItemDataModel } from '../../../../data-models/list-item.data-model';
import { ListType } from '../../../../data-models/list.data-model';
import { sectorsAtom, usersAtom, usersByEmailMapAtom } from '../../../../services/state/AppConfigStateJ';
import { authenticatedUserState } from '../../../../services/state/AuthState';
import { companySyncCacheAtom } from '../../../../services/state/CompanyStateJ';
import {
  activeCompanyListIdStateJ,
  companyListStateJ,
  listsByIdStateJ,
  togglePreviousDealsStateJ,
} from '../../../../services/state/ListsStateJ';
import { compareItems } from '../../utils/list-utils';
import { getFilteredList } from '../../utils/listFilteringUtils';

export const listSearchQueryStateJ = atom<string>('');

export const listSectorFilterStateJ = atom<number[] | null>(null);

export const listOwnerFilterStateJ = atom<number[] | null>(null);

/**
 * Reads {@link companyListStateJ} (sync) and the in-memory company cache
 * via {@link companySyncCacheAtom} (sync), so this atom is itself sync.
 */
export const companiesByListIdStateJ = atomFamily((listId: number) =>
  atom((get): Map<number, ICompanyDataModel> => {
    const listItems = get(companyListStateJ(listId))?.items;
    const companiesMap = new Map<number, ICompanyDataModel>();
    if (!listItems) {
      return companiesMap;
    }
    const companyCache = get(companySyncCacheAtom);
    for (const curr of listItems) {
      const comp = companyCache.get(curr.companyId);
      if (comp) {
        companiesMap.set(curr.companyId, comp);
      }
    }
    return companiesMap;
  })
);

/**
 * Async because it transitively reads {@link listsByIdStateJ}, which awaits
 * the async {@link listsStateJ}. All cross-atom reads go through `get`.
 */
export const filteredListStateJ = atomFamily((listId: number) =>
  atom(async (get): Promise<IListItemDataModel[] | null> => {
    const activeListId = get(activeCompanyListIdStateJ);
    const listMap = await get(listsByIdStateJ);
    const activeList = listMap.get(activeListId ?? -1);
    const listItems = get(companyListStateJ(listId))?.items;
    if (!listItems || !activeList) {
      return null;
    }

    const companiesMap = get(companiesByListIdStateJ(listId));
    const showPreviousDeals = get(togglePreviousDealsStateJ) || activeList.type !== ListType.DISCOVERY;

    const filteredSectors = get(listSectorFilterStateJ);
    const filteredOwners = get(listOwnerFilterStateJ);
    const searchQuery = get(listSearchQueryStateJ);
    const sectors = get(sectorsAtom);
    const users = get(usersAtom);

    return getFilteredList({
      items: listItems,
      searchQuery,
      filteredSectors,
      sectors,
      filteredOwners,
      users,
      companiesMap,
      showPreviousDeals,
    });
  })
);

export const filteredAndSortedListsStateJ = atomFamily((listId: number) =>
  atom(async (get): Promise<IListItemDataModel[] | null> => {
    const filteredList = await get(filteredListStateJ(listId));
    if (!filteredList) {
      return null;
    }
    const currentUser = get(authenticatedUserState);
    const usersByEmailMap = get(usersByEmailMapAtom);
    const companyCache = get(companySyncCacheAtom);

    const sorted = filteredList.map((item) => ({
      item,
      company: companyCache.get(item.companyId) ?? null,
    }));

    return sorted
      .sort((a, b) =>
        compareItems(a.item, b.item, a.company, b.company, usersByEmailMap.get(currentUser?.email ?? '')?.id)
      )
      .map(({ item }) => item);
  })
);
